"use client";

// ============================================================
// CLUBHUB – Helferschichten eines Termins
// ============================================================
// Der Zettel an der Kabinentuer, nur besser: jede Schicht mit Zeit,
// Bedarf und den Namen darauf, freie Plaetze als Luecke, in die man
// jemanden eintraegt. Oben die Summe und ein Zeitstrahl des Tages —
// wo die Abdeckung duenn ist, sieht man auf einen Blick.
//
// „Helfer anfragen“ soll den Eingeladenen einen Link schicken, ueber
// den man eine Schicht mit einem Tipp uebernimmt — kommt mit dem
// Nachrichtenmodul. Gaeste ohne Konto stehen (im Musterbestand) mit
// Namen da.
//
// Die Schichten sind `Shift`, die Helfer `ShiftAssignment`; jeder
// Eintrag wird sofort gespeichert (`app/(app)/kalender/actions.ts`).
// ============================================================
import { useMemo, useState } from "react";
import { HandHelping, Megaphone, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogRow,
  DialogSubmit,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownItem, DropdownMenu } from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { MehrKnopf, ZeichenKnopf } from "@/components/ui/zeichen-knopf";
import { Avatar } from "@/components/ui/avatar";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { TonBadge } from "@/components/mitglieder/status-badge";
import type { ActionResult } from "@/lib/action-result";
import { addDays, formatUhrzeiten, minutenZwischen, tagVon, uhrzeit, zeitpunkt } from "@/lib/kalender/tag";
import type { SchichtView } from "@/lib/kalender/views";
import { mockPersonen } from "@/lib/mock/kalender";
import { errorOf } from "@/lib/action-result";
import type { ContactView } from "@/lib/views";
import { helferAustragen, helferEintragen, schichtLoeschen, schichtSpeichern } from "@/app/(app)/kalender/actions";
import { cn } from "@/lib/utils";

type Name = { name: string; firstName: string; lastName: string };

/** Die Vorlage fuer ein Fest — dieselbe wie im Formular. */
export function festVorlage(tag: string, beginn: string, ende: string): Omit<SchichtView, "id">[] {
  const b = uhrzeit(beginn);
  const e = uhrzeit(ende);
  const minus = (hhmm: string, min: number) => {
    const t = Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5)) - min;
    const x = (t + 1440) % 1440;
    return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
  };
  const plus = (hhmm: string, min: number) => minus(hhmm, -min);
  const zp = (von: string, bis: string) => {
    const s = zeitpunkt(tag, von);
    let en = zeitpunkt(tag, bis);
    if (en <= s) en = zeitpunkt(addDays(tag, 1), bis);
    return { startsAt: s, endsAt: en };
  };
  return [
    { title: "Aufbau", ...zp(minus(b, 180), b), needed: 4, note: null, helfer: [] },
    { title: "Ausschank", ...zp(b, e), needed: 2, note: null, helfer: [] },
    { title: "Grill", ...zp(b, minus(e, 60)), needed: 2, note: null, helfer: [] },
    { title: "Abbau", ...zp(e, plus(e, 90)), needed: 4, note: null, helfer: [] },
  ];
}

export function Schichtplan({
  schichten: anfang,
  namen: anfangsNamen,
  tag,
  beginn,
  ende,
  serie,
  abgesagt,
  eingeladen,
  eventId,
  kontakte,
  schreiben = true,
}: {
  eventId: string;
  /** Personen fuer die Auswahl (im Musterbestand leer: Vorgabe der Auswahl). */
  kontakte?: ContactView[];
  /** Recht „Termine bearbeiten“ — ohne nur ansehen. */
  schreiben?: boolean;
  schichten: SchichtView[];
  namen: Record<string, Name>;
  tag: string;
  beginn: string;
  ende: string | null;
  serie: boolean;
  abgesagt: boolean;
  eingeladen: number;
}) {
  const [schichten, setSchichten] = useState(anfang);
  const [namen, setNamen] = useState(anfangsNamen);
  const [bearbeitet, setBearbeitet] = useState<SchichtView | "neu" | null>(null);
  const gesperrt = abgesagt || !schreiben;

  const plaetze = schichten.reduce((s, x) => s + x.needed, 0);
  const besetzt = schichten.reduce((s, x) => s + Math.min(x.needed, x.helfer.length), 0);
  const frei = plaetze - besetzt;

  const spanne = useMemo(() => {
    if (!schichten.length) return null;
    const von = schichten.map((s) => s.startsAt).sort()[0]!;
    const bis = schichten.map((s) => s.endsAt).sort().at(-1)!;
    return { von, bis, minuten: Math.max(60, minutenZwischen(von, bis)) };
  }, [schichten]);

  function nameFuer(id: string): Name | undefined {
    if (namen[id]) return namen[id];
    if (kontakte) {
      const c = kontakte.find((x) => x.id === id);
      return c ? { name: c.displayName, firstName: c.firstName ?? "", lastName: c.lastName ?? c.displayName } : undefined;
    }
    const p = mockPersonen([id])[0];
    return p ? { name: p.name, firstName: p.firstName, lastName: p.lastName } : undefined;
  }

  async function eintragen(schicht: SchichtView, id: string | null) {
    if (!id) return;
    const nameVon = nameFuer(id);
    if (schicht.helfer.some((h) => h.contactId === id)) return toast("Steht schon in dieser Schicht.");
    const doppelt = schichten.find((s) => s.id !== schicht.id && s.helfer.some((h) => h.contactId === id) && s.startsAt < schicht.endsAt && schicht.startsAt < s.endsAt);
    const sicherung = schichten;
    setSchichten((liste) => liste.map((s) => (s.id === schicht.id ? { ...s, helfer: [...s.helfer, { contactId: id }] } : s)));
    if (nameVon) setNamen((n) => ({ ...n, [id]: nameVon }));
    const r = await helferEintragen(schicht.id, id);
    const fehler = errorOf(r);
    if (fehler) {
      setSchichten(sicherung);
      return void toast.error(fehler);
    }
    const n = nameVon?.name ?? "Person";
    if (doppelt) toast.warning(`${n} ist zur selben Zeit schon bei „${doppelt.title}“ eingetragen.`);
    else toast.success(`${n} übernimmt „${schicht.title}“`);
  }

  async function austragen(schicht: SchichtView, index: number) {
    const h = schicht.helfer[index];
    const sicherung = schichten;
    setSchichten((liste) => liste.map((s) => (s.id === schicht.id ? { ...s, helfer: s.helfer.filter((_, i) => i !== index) } : s)));
    if (!h?.contactId) return;
    const r = await helferAustragen(schicht.id, h.contactId);
    const fehler = errorOf(r);
    if (fehler) {
      setSchichten(sicherung);
      toast.error(fehler);
    }
  }

  async function loeschen(schicht: SchichtView) {
    const ja = await confirmDialog({
      title: `Schicht „${schicht.title}“ löschen?`,
      description: schicht.helfer.length ? `${schicht.helfer.length} eingetragene Helfer werden benachrichtigt.` : "Es ist noch niemand eingetragen.",
      confirmLabel: "Schicht löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await schichtLoeschen(schicht.id);
    const fehler = errorOf(r);
    if (fehler) return void toast.error(fehler);
    setSchichten((liste) => liste.filter((s) => s.id !== schicht.id));
    toast.success("Schicht gelöscht");
  }

  async function anfragen() {
    const ja = await confirmDialog({
      title: `${frei} freie Plätze ausschreiben?`,
      description: `Die ${eingeladen} Eingeladenen bekommen eine Nachricht mit den offenen Schichten. Ein Tipp auf eine Schicht trägt sie ein — auch ohne Konto.`,
      confirmLabel: "Helfer anfragen",
    });
    if (!ja) return;
    // Mailing/Push mit Uebernahme-Links je offener Schicht: kommt mit dem Nachrichtenmodul.
    toast("Die Anfrage per Mail und Push kommt mit dem Nachrichtenmodul", { description: "Bis dahin Helfer hier direkt eintragen." });
  }

  async function vorlageUebernehmen() {
    const neu: SchichtView[] = [];
    for (const s of festVorlage(tag, beginn, ende ?? beginn)) {
      const r = await schichtSpeichern(eventId, null, { title: s.title, von: uhrzeit(s.startsAt), bis: uhrzeit(s.endsAt), needed: s.needed, note: s.note });
      const fehler = errorOf(r);
      if (fehler || !r.success) {
        toast.error(fehler ?? "Das hat nicht geklappt.");
        break;
      }
      neu.push({ ...s, id: r.id });
    }
    if (!neu.length) return;
    setSchichten(neu);
    toast.success("Vorlage übernommen", { description: "Aufbau, Ausschank, Grill und Abbau — Zeiten und Bedarf lassen sich anpassen." });
  }

  if (serie) {
    return (
      <Card>
        <CardContent className="p-5">
          <EmptyState
            icon={HandHelping}
            title="Helferschichten gibt es bei einzelnen Terminen"
            description="Für Serien wie das wöchentliche Training ist ein Schichtplan selten sinnvoll. Ein Fest, ein Turnier oder ein Arbeitseinsatz bekommt seinen eigenen Plan."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {schichten.length === 0 ? (
        <Card>
          <CardContent className="p-5">
            <EmptyState
              icon={HandHelping}
              title="Noch keine Helferschichten"
              description="Lege Schichten mit Zeit und Bedarf an — Mitglieder tragen sich mit einem Tipp ein, auch Eltern und Freunde ohne Konto."
              action={
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={() => setBearbeitet("neu")} disabled={gesperrt}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Schicht anlegen
                  </Button>
                  <Button variant="outline" onClick={vorlageUebernehmen} disabled={gesperrt}>
                    Vorlage für ein Fest
                  </Button>
                </div>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="space-y-4 p-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <div>
                  <p className="text-overline text-text-tertiary">Besetzt</p>
                  <p className="text-display-lg text-text-primary tabular-nums">
                    {besetzt}
                    <span className="text-heading-md text-text-tertiary"> / {plaetze}</span>
                  </p>
                </div>
                <div className="min-w-40 flex-1">
                  <p className="text-caption text-text-secondary mb-1 tabular-nums">{frei > 0 ? `${frei} Plätze frei` : "Alle Plätze besetzt"}</p>
                  <div className="bg-bg-tertiary h-2 overflow-hidden rounded-full">
                    <div className={cn("h-full rounded-full", frei > 0 ? "bg-warning" : "bg-success")} style={{ width: `${plaetze ? (besetzt / plaetze) * 100 : 0}%` }} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={anfragen} disabled={frei === 0 || gesperrt}>
                    <Megaphone className="h-4 w-4" aria-hidden />
                    Helfer anfragen
                  </Button>
                  <Button onClick={() => setBearbeitet("neu")} disabled={gesperrt}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Schicht
                  </Button>
                </div>
              </div>
              {spanne && (
                <div className="space-y-1.5 max-md:hidden" aria-label="Zeitstrahl der Schichten">
                  {schichten.map((s) => {
                    const links = (minutenZwischen(spanne.von, s.startsAt) / spanne.minuten) * 100;
                    const breite = (minutenZwischen(s.startsAt, s.endsAt) / spanne.minuten) * 100;
                    const voll = s.helfer.length >= s.needed;
                    return (
                      <div key={s.id} className="grid grid-cols-[10rem_1fr] items-center gap-3">
                        <span className="text-caption text-text-secondary truncate">{s.title}</span>
                        <div className="bg-bg-secondary relative h-5 rounded">
                          <div
                            className={cn("absolute inset-y-0 flex items-center rounded px-1.5", voll ? "bg-success-subtle" : "bg-warning-subtle")}
                            style={{ left: `${links}%`, width: `${Math.max(breite, 4)}%` }}
                            title={`${formatUhrzeiten(s.startsAt, s.endsAt, false)} · ${s.helfer.length}/${s.needed}`}
                          >
                            <span className={cn("truncate font-mono text-[10px] tabular-nums", voll ? "text-success" : "text-warning")}>
                              {s.helfer.length}/{s.needed}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-text-tertiary grid grid-cols-[10rem_1fr] gap-3 font-mono text-[10px] tabular-nums">
                    <span />
                    <span className="flex justify-between">
                      <span>{uhrzeit(spanne.von)}</span>
                      <span>{uhrzeit(spanne.bis)}</span>
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[...schichten]
              .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
              .map((s) => {
                const fehlt = Math.max(0, s.needed - s.helfer.length);
                return (
                  <Card key={s.id} className="flex flex-col">
                    <div className="flex items-start gap-2 px-4 pt-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-body-sm text-text-primary font-semibold">{s.title}</p>
                        <p className="text-caption text-text-tertiary font-mono tabular-nums">
                          {formatUhrzeiten(s.startsAt, s.endsAt, false)}
                          {tagVon(s.endsAt) > tagVon(s.startsAt) ? " (über Mitternacht)" : ""}
                        </p>
                      </div>
                      <TonBadge ton={fehlt ? "warning" : "success"}>
                        <span className="tabular-nums">{fehlt ? `${fehlt} frei` : "besetzt"}</span>
                      </TonBadge>
                      {schreiben && (
                        <DropdownMenu trigger={<MehrKnopf groesse="klein" label={`Aktionen für ${s.title}`} />}>
                          <DropdownItem onClick={() => setBearbeitet(s)}>
                            <Pencil className="text-text-tertiary h-4 w-4" aria-hidden />
                            Bearbeiten
                          </DropdownItem>
                          <DropdownItem variant="danger" onClick={() => loeschen(s)}>
                            <Trash2 className="h-4 w-4" aria-hidden />
                            Löschen
                          </DropdownItem>
                        </DropdownMenu>
                      )}
                    </div>
                    {s.note && <p className="text-caption text-text-secondary px-4 pt-1">{s.note}</p>}
                    <ul className="flex-1 space-y-1.5 px-4 pt-3 pb-4">
                      {s.helfer.map((h, i) => {
                        const n = h.contactId ? namen[h.contactId] : null;
                        const name = n?.name ?? h.name ?? "Unbekannt";
                        return (
                          <li key={`${h.contactId ?? h.name}-${i}`} className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5", i < s.needed ? "bg-bg-secondary" : "bg-warning-subtle")}>
                            <Avatar firstName={n?.firstName ?? name} lastName={n?.lastName ?? ""} size="sm" />
                            <span className="text-body-sm text-text-primary min-w-0 flex-1 truncate">{name}</span>
                            {!h.contactId && <TonBadge ton="info">Gast</TonBadge>}
                            {i >= s.needed && <span className="text-caption text-warning">über Bedarf</span>}
                            <ZeichenKnopf label={`${name} austragen`} groesse="klein" onClick={() => austragen(s, i)} disabled={gesperrt}>
                              <X className="h-3.5 w-3.5" aria-hidden />
                            </ZeichenKnopf>
                          </li>
                        );
                      })}
                      {Array.from({ length: fehlt }, (_, i) => (
                        <li key={`frei-${i}`} className="border-border-control rounded-lg border border-dashed p-0.5">
                          <KontaktAuswahl
                            value={null}
                            onChange={(id) => eintragen(s, id)}
                            placeholder="Platz frei — Person eintragen"
                            kontakte={kontakte}
                            className="border-0 bg-transparent"
                            disabled={gesperrt}
                          />
                        </li>
                      ))}
                    </ul>
                  </Card>
                );
              })}
          </div>
        </>
      )}

      {bearbeitet && (
        <SchichtDialog
          eventId={eventId}
          schicht={bearbeitet === "neu" ? null : bearbeitet}
          tag={tag}
          vorgabe={{ von: uhrzeit(beginn), bis: ende ? uhrzeit(ende) : uhrzeit(beginn) }}
          onClose={() => setBearbeitet(null)}
          onSpeichern={(s) => setSchichten((liste) => (liste.some((x) => x.id === s.id) ? liste.map((x) => (x.id === s.id ? s : x)) : [...liste, s]))}
        />
      )}
    </div>
  );
}

function SchichtDialog({
  eventId,
  schicht,
  tag,
  vorgabe,
  onClose,
  onSpeichern,
}: {
  eventId: string;
  schicht: SchichtView | null;
  tag: string;
  vorgabe: { von: string; bis: string };
  onClose: () => void;
  onSpeichern: (s: SchichtView) => void;
}) {
  const [fehler, setFehler] = useState<Record<string, string>>({});

  async function speichern(form: FormData): Promise<ActionResult> {
    const title = String(form.get("title") ?? "").trim();
    const von = String(form.get("von") ?? "");
    const bis = String(form.get("bis") ?? "");
    const needed = Number(form.get("needed"));
    const note = String(form.get("note") ?? "").trim();
    const f: Record<string, string> = {};
    if (!title) f.title = "Wie heißt die Schicht?";
    if (!von) f.von = "Beginn fehlt.";
    if (!bis) f.bis = "Ende fehlt.";
    if (!Number.isInteger(needed) || needed < 1 || needed > 99) f.needed = "Zwischen 1 und 99 Personen.";
    if (von && bis && von === bis) f.bis = "Beginn und Ende sind gleich.";
    setFehler(f);
    const erster = Object.entries(f)[0];
    if (erster) return { error: erster[1], field: erster[0] };
    const startsAt = zeitpunkt(tag, von);
    let endsAt = zeitpunkt(tag, bis);
    if (endsAt <= startsAt) endsAt = zeitpunkt(addDays(tag, 1), bis);
    const r = await schichtSpeichern(eventId, schicht?.id ?? null, { title, von, bis, needed, note: note || null });
    if (errorOf(r) || !r.success) return r;
    onSpeichern({ id: r.id, title, startsAt: r.startsAt || startsAt, endsAt: r.endsAt || endsAt, needed, note: note || null, helfer: schicht?.helfer ?? [] });
    return { success: true };
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{schicht ? "Schicht bearbeiten" : "Neue Schicht"}</DialogTitle>
        <DialogDescription>Endet sie vor dem Beginn, geht sie über Mitternacht (Abbau bis 01:00).</DialogDescription>
      </DialogHeader>
      <DialogForm action={speichern} refresh={false} onError={() => undefined} success={schicht ? "Schicht gespeichert" : "Schicht angelegt"}>
        <DialogContent>
          <Input name="title" label="Bezeichnung" defaultValue={schicht?.title ?? ""} placeholder="z. B. Ausschank, Kasse, Kuchentheke" error={fehler.title} />
          <DialogRow columns={3}>
            <Input name="von" type="time" label="Beginn" defaultValue={schicht ? uhrzeit(schicht.startsAt) : vorgabe.von} error={fehler.von} />
            <Input name="bis" type="time" label="Ende" defaultValue={schicht ? uhrzeit(schicht.endsAt) : vorgabe.bis} error={fehler.bis} />
            <Input name="needed" type="number" inputMode="numeric" min={1} max={99} label="Bedarf" defaultValue={schicht?.needed ?? 2} error={fehler.needed} className="tabular-nums" />
          </DialogRow>
          <Input name="note" label="Hinweis" defaultValue={schicht?.note ?? ""} placeholder="z. B. Gasflasche liegt im Lager" hint="Sehen alle, die sich eintragen." />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>{schicht ? "Speichern" : "Schicht anlegen"}</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}
